/**
 * Merk-primitief — herkomst versus uitvoerder.
 *
 * ═══ DE KLASSE ═══
 * `Brand__c` betekent herkomst. De SF-velddefinitie is er expliciet over:
 * "het merk/brand waarmee de Lead is binnengekomen", helptekst "selecteer het
 * merk via welke de klant binnenkwam". Maar een groeiend aantal consumenten
 * leest het als uitvoerder: welke mailbox, welk briefpapier, welke stem, welk
 * portaal. Voor KLEDA/CINCO/SOFASAVER/SCHOONHUISSHOP vallen die twee samen,
 * voor de satellietsites niet.
 *
 * Gevolg: elke map die maar vier merken kent breekt of liegt op een
 * satelliet-order. Gemeten 21-08: 237 van de 649 werkorders in augustus staan
 * op VLOERKLEEDREINIGEN, en de mail-composer gaf er een harde 400 op omdat
 * MAILBOXES het merk niet kent.
 *
 * Dit is de derde keer dat dezelfde klasse toeslaat. Eerder:
 *  - 22-07 t/m 26-07: 58 door Kleda uitgevoerde orders (binnengekomen als
 *    VLOERKLEEDREINIGEN of CINCO) kregen de Cinco-mail "de specialist neemt
 *    contact op" terwijl de ophaalrit al stond. Gepatcht met `isKledaPad`,
 *    maar alleen op dat ene pad (`convert/convert-mails.ts`).
 *  - 31-07: factuur-briefpapier, gepatcht met `bepaalInvoiceBrand`
 *    (`finance/invoice-kernen.ts`), ook alleen op dat pad.
 *
 * ═══ DE REGEL ═══
 * Herkomst blijft staan zoals hij is en wordt nooit overschreven: dat is de
 * enige plek waar zichtbaar is hoeveel volume een satellietsite oplevert.
 * Het uitvoerende merk wordt afgeleid, hier, en nergens anders opnieuw.
 *
 * De partner is de echte discriminator, niet het merk. Dat is de les van de
 * juli-regressie en de reden dat `partnerNaam` vóór alles gaat.
 *
 * ═══ BRON VAN WAARHEID ═══
 * Byte-identieke spiegel (Vercel-functions kunnen niet buiten hun app-root
 * importeren, Vite-apps geen workspace-deps). Wijzig je iets, wijzig dan
 * BEIDE en draai de pariteitstest:
 *   1. brands/cinco/apps/office-app/api/_lib/merk.ts   (dit bestand)
 *   2. brands/cinco/apps/office-app/src/lib/merk.ts
 *   3. SQL-spiegel voor de meting: functie uitvoerend_merk(mirror_lead) in
 *      shared/supabase/migrations/231_uitvoerend_merk_op_lead.sql
 *
 * De test `api/_lib/__tests__/merk.test.ts` bewaakt bovendien dat MAILBOXES,
 * AI_BRAND_CONFIG en deze lijsten dezelfde verzameling dekken. Een zevende
 * merk zonder mailbox faalt daar luid, in plaats van stil in productie.
 */

/** Merk waarmee de klant binnenkwam. Gelijk aan de SF-valueset `Brand`. */
export type HerkomstMerk =
  | 'KLEDA'
  | 'CINCO'
  | 'VLOERKLEEDREINIGEN'
  | 'BANKREINIGEN'
  | 'SOFASAVER'
  | 'SCHOONHUISSHOP';

/** Merk waaronder we uitvoeren, factureren en communiceren. Heeft een mailbox. */
export type UitvoerendMerk = 'KLEDA' | 'CINCO' | 'SOFASAVER' | 'SCHOONHUISSHOP';

export const HERKOMST_MERKEN: readonly HerkomstMerk[] = [
  'KLEDA',
  'CINCO',
  'VLOERKLEEDREINIGEN',
  'BANKREINIGEN',
  'SOFASAVER',
  'SCHOONHUISSHOP',
] as const;

export const UITVOERENDE_MERKEN: readonly UitvoerendMerk[] = [
  'KLEDA',
  'CINCO',
  'SOFASAVER',
  'SCHOONHUISSHOP',
] as const;

/**
 * Satellietsites: lead-gen, nooit zelf uitvoerder.
 *
 * vloerkleedreinigen.com verkoopt wasserij-werk dat Kleda zelf draait, met een
 * Kleda-bus, een Kleda-factuur en Kleda-mail. bankreinigen.nl verkoopt
 * meubelreiniging op locatie, en dat gaat via het Cinco-partnernetwerk.
 */
export const SATELLIET_NAAR_UITVOERDER: Readonly<Record<string, UitvoerendMerk>> = {
  VLOERKLEEDREINIGEN: 'KLEDA',
  BANKREINIGEN: 'CINCO',
};

/**
 * Standaard als niets uitsluitsel geeft. Kleda is vrijwel het hele volume, en
 * dit is dezelfde default als `bepaalInvoiceBrand` en de telefonie-agent.
 */
export const DEFAULT_UITVOEREND_MERK: UitvoerendMerk = 'KLEDA';

/** Recordtypes die op het Cinco-platform thuishoren (spiegel van bepaalInvoiceBrand). */
const RECORDTYPE_NAAR_CINCO = new Set(['SofaSaver', 'Cinco', 'SR']);

export function isHerkomstMerk(waarde: string | null | undefined): waarde is HerkomstMerk {
  return HERKOMST_MERKEN.includes((waarde ?? '').trim().toUpperCase() as HerkomstMerk);
}

export function isUitvoerendMerk(waarde: string | null | undefined): waarde is UitvoerendMerk {
  return UITVOERENDE_MERKEN.includes((waarde ?? '').trim().toUpperCase() as UitvoerendMerk);
}

/** Is dit merk een satellietsite en dus per definitie niet de uitvoerder? */
export function isSatellietMerk(waarde: string | null | undefined): boolean {
  return (waarde ?? '').trim().toUpperCase() in SATELLIET_NAAR_UITVOERDER;
}

export interface UitvoerendMerkInput {
  /** `Brand__c` van de lead, offerte of werkorder. */
  herkomstMerk: string | null | undefined;
  /** RecordType.DeveloperName van de werkorder, als bekend. */
  recordType?: string | null;
  /** `Partner__r.Name`. Wint van alles: de partner voert daadwerkelijk uit. */
  partnerNaam?: string | null;
}

/**
 * Onder welk merk voeren we deze order uit, mailen we en factureren we.
 *
 * Volgorde is bewust en volgt `isKledaPad`: partner eerst, want dat is de
 * enige harde uitspraak over wie het werk doet. Pas daarna het merk, dat
 * alleen een snelkoppeling is voor het gewone verkeer.
 */
export function bepaalUitvoerendMerk(input: UitvoerendMerkInput): UitvoerendMerk {
  const partner = (input.partnerNaam ?? '').trim();
  if (partner === 'Kleda') return 'KLEDA';

  const merk = (input.herkomstMerk ?? '').trim().toUpperCase();
  if (isUitvoerendMerk(merk)) return merk as UitvoerendMerk;

  const satelliet = SATELLIET_NAAR_UITVOERDER[merk];
  if (satelliet) return satelliet;

  // Onbekend of leeg merk: het recordtype is het laatste signaal.
  if (RECORDTYPE_NAAR_CINCO.has(input.recordType ?? '')) return 'CINCO';
  return DEFAULT_UITVOEREND_MERK;
}

/**
 * Leesbare herkomst voor kantoor, zodat het merk-veld niet uit beeld verdwijnt
 * zodra we op uitvoerder schakelen. Null als de herkomst niets toevoegt (de
 * herkomst is dan gelijk aan de uitvoerder).
 */
export function herkomstToelichting(
  herkomstMerk: string | null | undefined
): string | null {
  const merk = (herkomstMerk ?? '').trim().toUpperCase();
  const labels: Record<string, string> = {
    VLOERKLEEDREINIGEN: 'vloerkleedreinigen.com',
    BANKREINIGEN: 'bankreinigen.nl',
  };
  return labels[merk] ?? null;
}
